import { useRef } from 'react'
import {
  ContentCopy,
  Delete,
  MoreHoriz,
  VerticalAlignBottom,
  VerticalAlignTop,
} from '@mui/icons-material'
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { DocumentRow } from '@/types/documents'
import { showInfoSnackbar } from '@/utils/snackbars'
import copyToClipboard from '@/components/document-actions-menu/copy-to-clipboard'

export type FieldTableRowActionsMenuProps = {
  documentRow: DocumentRow
  addRow: ({
    rowId,
    position,
    shouldFocus,
  }: {
    rowId?: string
    position?: 'before' | 'after'
    shouldFocus?: boolean
  }) => void
  deleteDocumentRows: (rowIds: string[]) => void
}

export default function FieldTableRowActionsMenu({
  documentRow,
  addRow,
  deleteDocumentRows,
}: FieldTableRowActionsMenuProps) {
  const menuOverlay = useOverlay()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isSuperUser = useIsSuperUser()

  const handleCopyId = () => {
    copyToClipboard(documentRow.id)
    showInfoSnackbar('Copied to Clipboard')
    menuOverlay.close()
  }

  return (
    <>
      <Tooltip title="Row Actions" enterDelay={1000}>
        <IconButton
          ref={buttonRef}
          onClick={menuOverlay.open}
          size="small"
          tabIndex={-1}
        >
          <MoreHoriz fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Menu
        MenuListProps={{
          dense: true,
        }}
        anchorEl={buttonRef.current}
        open={menuOverlay.isOpen}
        onClose={menuOverlay.close}
      >
        <MenuItem
          onClick={() => {
            addRow({
              rowId: documentRow.id,
              position: 'before',
              shouldFocus: true,
            })
            menuOverlay.close()
          }}
        >
          <ListItemIcon>
            <VerticalAlignTop />
          </ListItemIcon>
          <ListItemText>Insert Row Above</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            addRow({
              rowId: documentRow.id,
              position: 'after',
              shouldFocus: true,
            })
            menuOverlay.close()
          }}
        >
          <ListItemIcon>
            <VerticalAlignBottom />
          </ListItemIcon>
          <ListItemText>Insert Row Below</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            deleteDocumentRows([documentRow.id])
            menuOverlay.close()
          }}
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
        {isSuperUser && (
          <MenuItem
            onClick={handleCopyId}
            sx={{ color: SUPER_USER_ONLY_COLOR }}
          >
            <ListItemIcon>
              <ContentCopy sx={{ color: SUPER_USER_ONLY_COLOR }} />
            </ListItemIcon>
            <ListItemText>Copy ID</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
